import { useMemo } from 'react';

import {
  ExperimentViewOverridesSchema,
  FeedbackConfigSchema,
  FeedbackStatsField,
} from '@/types/schema';

import {
  HEATMAP_COLORS,
  getHeatmapStyleFromPercentage,
} from '../../utils/getHeatmapStyleFromPercentage';

export const useFeedbackStyling = ({
  showHeatmap,
  allFeedbackStats,
  isDarkMode,
  feedbackConfig,
  columnOverride,
}: {
  showHeatmap: boolean;
  allFeedbackStats: FeedbackStatsField[];
  isDarkMode: boolean;
  feedbackConfig?: FeedbackConfigSchema;
  columnOverride?: ExperimentViewOverridesSchema['column_overrides'][number];
}) => {
  return useMemo(() => {
    if (!showHeatmap) return { text: '', background: '', hover: '' };
    if (!allFeedbackStats.length)
      return { text: '', background: '', hover: '' };
    // all errors
    if (!allFeedbackStats.some((stats) => !stats.feedback_stats?.errors))
      return HEATMAP_COLORS.middle[isDarkMode ? 'dark' : 'light'];
    const minScore =
      typeof feedbackConfig?.feedback_config.min === 'number'
        ? feedbackConfig?.feedback_config.min
        : allFeedbackStats[0]?.feedback_stats?.session_min_score;
    const maxScore =
      typeof feedbackConfig?.feedback_config.max === 'number'
        ? feedbackConfig?.feedback_config.max
        : allFeedbackStats[0]?.feedback_stats?.session_max_score;

    const avgScores = allFeedbackStats
      .map((fs) => fs.feedback_stats?.avg)
      .filter((avg): avg is number => avg != null);

    if (
      avgScores.length === 0 ||
      (columnOverride?.color_gradient === undefined &&
        minScore === undefined &&
        maxScore === undefined)
    ) {
      return { text: '', background: '', hover: '' };
    }

    const { text, background, hover } = getHeatmapStyleFromPercentage({
      colorMode: isDarkMode ? 'dark' : 'light',
      min: minScore,
      max: maxScore,
      score: avgScores.reduce((sum, avg) => sum + avg, 0) / avgScores.length,
      isLowerBetter:
        columnOverride?.color_gradient?.[0]?.[1] === 'green' ||
        !!feedbackConfig?.is_lower_score_better,
      colorGradient: columnOverride?.color_gradient,
    });
    return { text, background, hover };
  }, [
    allFeedbackStats,
    isDarkMode,
    showHeatmap,
    feedbackConfig,
    columnOverride,
  ]);
};
